import { ArticleLayout } from '#app/features/writing/article-layout.tsx'
import { LedgerDemo } from '#app/features/writing/components/ledger-demo.tsx'

export const article = {
	title: 'What is deterministic accounting software?',
	date: '2026-06-07',
	description:
		'Most accounting software stores rows you edit. A deterministic engine stores the inputs and the rules and regenerates the ledger from them — so the same inputs give the same ledger every time, and every row can name what produced it.',
	slug: 'declarative-accounting',
	draft: false,
	hardProblem:
		'The ledger is only as right as the rules are complete, and an incomplete rule set does not fail loudly. Miss one cross-chain equivalence and a bridge hop books as a disposal and a fresh acquisition — balanced, plausible, and wrong.',
	approach:
		'Declare the inputs and the rules; derive the ledger from them. Every row keeps the input and rule that produced it, so a wrong number leads back to the gap in the declaration rather than to a hunt through the rows.',
	tags: ['Ledger engine', 'Accounting'],
}

export default (props) => <ArticleLayout article={article} {...props} />

The traditional model is **imperative**: someone posts each journal entry by hand,
and the ledger _is_ those stored rows. **Deterministic** accounting flips that —
you declare the inputs and the rules, and the ledger is _derived_ from
them. You never hand-edit a row; you change an input or a rule and regenerate the
whole thing. The piece doing the deriving is a generator, a pure function from
immutable inputs to a full double-entry ledger.

```ts
function generateLedger(inputs: Input[]): LedgerRow[] {
	const rows: LedgerRow[] = []
	for (const input of inputs) {
		rows.push(...rulesFor(input).map((rule) => rule.apply(input)))
	}
	return rows
}
```

## The work is in the declaration

Moving an asset across chains should net to nothing, but the generator can only
see that if the two chain-local assets are declared equivalent. Miss the entry and
the outbound leg books as a disposal, the inbound as a fresh acquisition at market
value, and nothing objects. Double-entry catches a whole class of error for free —
value cannot appear or vanish without the other leg — which is precisely why every
error that survives it balances perfectly. The declaration is where the domain
knowledge lives, and it is the thing that has to be maintained.

## Why provenance is the mitigation

Every row carries the input and the rule that produced it, so a number that looks
wrong resolves to a specific rule that was missing or mis-stated, rather than to a
search through rows for the one somebody edited. You fix the declaration and
regenerate; you never patch the output.

## Scenarios come free

Because the ledger is a function of its inputs and rules, changing a rule is a
scenario rather than a migration. A different cost-basis method, or a treatment
you are not yet sure about, is a second run and a diff — two ledgers from the same
events, compared, rather than one book edited into a new shape.

<LedgerDemo />

Deriving the ledger is half of it. How it changes once derived — corrections
appended beside mistakes rather than overwriting them — is the
[immutable ledger](/writing/immutable-ledger).
